import React from 'react';
import { Link } from 'react-router-dom';
import { translateKeyLang } from '../../../../common/src/translate/translateHelper';
import { fetchGet } from '../../store/fetch';
import { InspectionStandardTypes } from '../../utils/enums/InspectionStandardTypes';
import { formatDate } from '../../utils/formatDate';
import { appContext } from '../appContext';
import { EditPanelCell, EditPanelRow, EditPanelSelect, PanelBody, Topbar, TopbarCrumb, TopbarCrumbActive, TopbarLeft, Tray } from '../elements';
import { Spinner } from '../spinners';
import { Component } from '../utils/Component';
import { DisplayMessage } from '../utils/displayMessage';
import { loadSurveyElements } from './Common/loadSurveyElements';
import { IElement } from './Interfaces/IElement';
import { ISameTypeSurveysForObject } from './Interfaces/ISameTypeSurveysForObject';
import { ConditionReportNEN } from './NEN/ConditionReportNEN';

interface ISurveyCompareProps {
    match: { params: { id?: string } };
}

interface ISurveyCompareState {
    breakDownOpenState: { [key: string]: boolean };
    description?: string;
    elements: IElement[];
    elementsSurveyToCompare: IElement[];
    inspectionStandardType?: InspectionStandardTypes;
    isLoadingElements: boolean;
    isLoadingElementsSurveyToCompare: boolean;
    isLoadingSurveyDetails: boolean;
    isSurveyToCompareAvailable: boolean;
    noData: boolean;
    objectConstructionYear?: number;
    objectId?: string;
    sameTypeSurveysForObject: ISameTypeSurveysForObject[];
    surveryedOn?: Date;
    indexSurveyToCompare: number;
}

export class SurveyCompare extends Component<ISurveyCompareProps, ISurveyCompareState> {
    constructor(props: ISurveyCompareProps) {
        super(props);

        this.state = {
            isLoadingSurveyDetails: true,
            isLoadingElements: false,
            isLoadingElementsSurveyToCompare: false,
            isSurveyToCompareAvailable: false,
            breakDownOpenState: {},
            elements: [],
            elementsSurveyToCompare: [],
            sameTypeSurveysForObject: [],
            noData: false,
            indexSurveyToCompare: 0,
        };
    }

    public override async componentDidMount() {
        await this.load();
    }

    public override render() {
        const isFree = this.state.inspectionStandardType === InspectionStandardTypes.Free;
        const isNen = this.state.inspectionStandardType === InspectionStandardTypes.Nen2767;
        return (
            <appContext.Consumer>
                {(state) => (
                    <>
                        <Topbar>
                            <TopbarLeft>
                                <TopbarCrumbActive title={translateKeyLang(state.languageCode, 'survey.compare')} icon="fa-exchange" />
                                <TopbarCrumb>
                                    <Link to={`/surveys/${this.props.match.params.id}`}>
                                        {translateKeyLang(state.languageCode, 'common.surveyDetails')}
                                    </Link>
                                </TopbarCrumb>
                                <TopbarCrumb>
                                    <Link to={`/objects/${this.state.objectId}`}>{translateKeyLang(state.languageCode, 'common.objectDetails')}</Link>
                                </TopbarCrumb>
                                <TopbarCrumb>
                                    <Link to="/objects">{translateKeyLang(state.languageCode, 'common.objects')}</Link>
                                </TopbarCrumb>
                            </TopbarLeft>
                        </Topbar>
                        <Tray padding>
                            {this.state.isLoadingSurveyDetails && <Spinner />}

                            {this.state.noData && (
                                <DisplayMessage message={translateKeyLang(state.languageCode, 'survey.notAvailable')} messageType="noDetailsData" />
                            )}

                            {this.state.sameTypeSurveysForObject.length > 1 && (
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}></EditPanelCell>
                                    <EditPanelCell rowCount={6}>
                                        <PanelBody isForm mb>
                                            <EditPanelSelect
                                                name="indexSurveyToCompare"
                                                placeholder={translateKeyLang(state.languageCode, 'survey.selectSurveyToCompare')}
                                                value={this.state.indexSurveyToCompare}
                                                handleChange={async (value) => this.handleChange(value)}
                                                options={this.getSurveysToCompare(state.languageCode)}
                                            />
                                        </PanelBody>
                                    </EditPanelCell>
                                </EditPanelRow>
                            )}

                            <EditPanelRow>
                                <EditPanelCell rowCount={6}>
                                    {/* condition report */}
                                    {(isFree || isNen) && (
                                        <ConditionReportNEN
                                            breakDownOpenState={this.state.breakDownOpenState}
                                            constructionYear={this.state.objectConstructionYear}
                                            elements={this.state.elements}
                                            isAssetDescriptionVisible={true}
                                            isCareScoreAvailable={isNen}
                                            isLoadingElements={this.state.isLoadingElements}
                                            isNen={isNen}
                                            isCompareView={true}
                                            maintenanceMeasures={[]}
                                            onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                            onCollapseAll={this.onCollapseAll.bind(this)}
                                            onExpandAll={this.onExpandAll.bind(this)}
                                            onExpandDecomposition={this.onExpandDecomposition.bind(this)}
                                            surveyId={this.props.match.params.id}
                                            title={`${formatDate(state.languageCode, this.state.surveryedOn)}, ${this.state.description}`}
                                        />
                                    )}
                                </EditPanelCell>

                                <EditPanelCell rowCount={6}>
                                    {/* condition report */}
                                    {(isFree || isNen) && this.state.isSurveyToCompareAvailable && (
                                        <ConditionReportNEN
                                            breakDownOpenState={this.state.breakDownOpenState}
                                            constructionYear={this.state.objectConstructionYear}
                                            elements={this.state.elementsSurveyToCompare}
                                            isAssetDescriptionVisible={true}
                                            isCareScoreAvailable={isNen}
                                            isLoadingElements={this.state.isLoadingElementsSurveyToCompare}
                                            isNen={isNen}
                                            isCompareView={true}
                                            maintenanceMeasures={[]}
                                            onChangeBreakdownOpenState={this.onChangeBreakdownOpenState.bind(this)}
                                            onCollapseAll={this.onCollapseAll.bind(this)}
                                            onExpandAll={this.onExpandAll.bind(this)}
                                            onExpandDecomposition={this.onExpandDecomposition.bind(this)}
                                            surveyId={this.state.sameTypeSurveysForObject[this.state.indexSurveyToCompare]!.id}
                                            title={`${formatDate(
                                                state.languageCode,
                                                this.state.sameTypeSurveysForObject[this.state.indexSurveyToCompare]!.surveryedOn,
                                            )}, ${this.state.sameTypeSurveysForObject[this.state.indexSurveyToCompare]!.description}`}
                                        />
                                    )}
                                </EditPanelCell>
                            </EditPanelRow>
                        </Tray>
                    </>
                )}
            </appContext.Consumer>
        );
    }

    private async load() {
        const { id: surveyId } = this.props.match.params;
        if (!surveyId) {
            return;
        }
        const url = `/api/surveys/${surveyId}`;
        const result = await fetchGet(url, {}, ['UNKNOWN_SURVEY_OR_NO_AUTH'], [403]);
        if (result.status === 403) {
            await this.setState({ noData: true, isLoadingSurveyDetails: false });
        } else {
            await this.setState(result.json);
            await this.setState({ isLoadingSurveyDetails: false, isLoadingElements: true });
            const elements = await loadSurveyElements(surveyId, this.state.objectId, this.state.inspectionStandardType);
            await this.setState({ elements, isLoadingElements: false });

            // If we have only one other survey to compare we directly load these elements and show the survey
            if (this.state.sameTypeSurveysForObject.length === 1) {
                await this.loadSurveyToCompare();
            }
        }
    }

    private async loadSurveyToCompare() {
        if (this.state.sameTypeSurveysForObject.length > 0) {
            await this.setState({ isLoadingElementsSurveyToCompare: true, isSurveyToCompareAvailable: true });
            const elementsSurveyToCompare = await loadSurveyElements(
                this.state.sameTypeSurveysForObject[this.state.indexSurveyToCompare]!.id,
                this.state.sameTypeSurveysForObject[this.state.indexSurveyToCompare]!.objectId,
                this.state.sameTypeSurveysForObject[this.state.indexSurveyToCompare]!.inspectionStandardType,
            );
            await this.setState({ elementsSurveyToCompare, isLoadingElementsSurveyToCompare: false });
        }
    }

    private getSurveysToCompare(languageCode: string) {
        return this.state.sameTypeSurveysForObject.map((survey, i) => ({
            value: i.toString(),
            text: `${formatDate(languageCode, survey.surveryedOn)} ${survey.description}`,
        }));
    }

    private async handleChange(value: any) {
        if (value === '') {
            await this.setState({ isSurveyToCompareAvailable: false, indexSurveyToCompare: -1 });
        } else {
            await this.setState({ isSurveyToCompareAvailable: true, indexSurveyToCompare: value });
            await this.loadSurveyToCompare();
        }
    }

    private onChangeBreakdownOpenState(elementId: string) {
        const { breakDownOpenState } = this.state;
        breakDownOpenState[elementId] = !breakDownOpenState[elementId];
        void this.setState({ breakDownOpenState });
    }

    private onExpandAll() {
        const breakDownOpenState = {} as { [key: string]: boolean };
        for (const element of this.state.elements) {
            breakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                breakDownOpenState[unit.id] = true;
                for (const manifestation of unit.manifestations) {
                    breakDownOpenState[manifestation.id] = true;
                }
            }
        }
        void this.setState({ breakDownOpenState });
    }

    private onExpandDecomposition() {
        const breakDownOpenState = {} as { [key: string]: boolean };
        for (const element of this.state.elements) {
            breakDownOpenState[element.id] = true;
            for (const unit of element.units) {
                if (unit.manifestations.length !== 0) {
                    breakDownOpenState[unit.id] = true;
                }
            }
        }
        void this.setState({ breakDownOpenState });
    }

    private onCollapseAll() {
        void this.setState({ breakDownOpenState: {} });
    }
}
